/**
 * iPadOS detection for audio telemetry.
 *
 * The only platform fact the audio telemetry has to work out for itself. GA4
 * and Sentry both derive browser, browser version and OS from the User-Agent on
 * their own, so stamping those onto an episode records them twice. The iPad is
 * the exception: iPadOS 13+ reports a desktop-Macintosh UA, so both sinks file
 * an iPad as a Mac. An iPad runs the same WebKit audio stack an iPhone does and
 * a Mac does not, which makes it the one distinction the sinks get wrong and
 * the one we have to measure.
 *
 * A real Mac and an iPad differ only in touch points, so that is what this
 * keys on.
 */

/** The bits of `navigator` this probe reads. */
export interface PlatformProbe {
  userAgent: string
  maxTouchPoints: number
}

/** Pure resolver — takes the probe so it can be unit-tested without a DOM. */
export const isIPadOSFrom = (probe: PlatformProbe): boolean => {
  if (/iPad/.test(probe.userAgent)) return true
  return /Macintosh/.test(probe.userAgent) && probe.maxTouchPoints > 1
}

/**
 * Whether the current environment is an iPad. False when there is no
 * `navigator` (tests, SSR) — the telemetry treats it as "not an iPad" rather
 * than a third state, since nothing branches on the difference.
 */
export const isIPadOS = (): boolean => {
  if (typeof navigator === 'undefined') return false
  return isIPadOSFrom({
    userAgent: navigator.userAgent ?? '',
    maxTouchPoints: navigator.maxTouchPoints ?? 0,
  })
}
